import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
import moment from "moment";
// material
import {
  Box,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
  Stack
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

// components
import Page from "../../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import { useSelector } from "react-redux";
import { medicalInsurancePostdocUpload } from "../../../core/service/medicalInsurancePostdoc.service";
import Swal from "sweetalert2";

// ----------------------------------------------------------------------
// const ROLE_ADMIN = "admin";
// const ROLE_MENTOR = "mentor";
// const ROLE_STD = "std";

export default function MedicalInsurance() {
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const navigate = useNavigate();

  const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d);
  };

  const FormMedicalInsuranceSchema = Yup.object().shape({
    name_company_medical_insurance: Yup.string().required("Is required"),
    expiration_date_insurance: Yup.string()
      .nullable()
      .required(`Expired Date Is required`)
      .test("expiration_date_insurance", "Expired date is not valid", (val) => {
        if (val) {
          const startDate = new Date();
          const endDate = new Date(2050, 12, 31);
          if (moment(val, moment.ISO_8601).isValid()) {
            return moment(val).isBetween(startDate, endDate);
          }
          return false;
        }
        return false;
      }),
    period_medical_insurance_c2f_scholarship:
      Yup.string().required("is required"),
    insurance_card: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    insurance_policy: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    others_evidence_medical_insurance: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable(),
    remarks: Yup.string()
  });

  const formik = useFormik({
    initialValues: {
      name_company_medical_insurance: "",
      expiration_date_insurance: {
        file: {}
      },
      period_medical_insurance_c2f_scholarship: "",
      insurance_card: {
        file: {}
      },
      insurance_policy: {
        file: {}
      },
      others_evidence_medical_insurance: {
        file: {}
      },
      remarks: ""
    },
    validationSchema: FormMedicalInsuranceSchema,
    onSubmit: async (values) => {
      var formData = new FormData();
      for (const [key, value] of Object.entries(values)) {
        if (value?.file) {
          formData.append(key, value.file);
        } else {
          formData.append(key, value);
        }
      }
      formData.append("user_id", currentUser.id);

      await medicalInsurancePostdocUpload(formData).then((e) => {
        return Swal.fire({
          icon: "success",
          title: "Successfully",
          showConfirmButton: false,
          timer: 1500
        }).then((result) =>
          navigate("/profile/post_doc", { replace: true })
        );
      });
      await new Promise((r) => setTimeout(r, 500));
    }
  });

  const {
    errors,
    getFieldProps,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    setFieldValue
  } = formik;

  return (
    <Page title="Medical insurance declaration form">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">
            Medical Insurance Declaration Form
          </Typography>
        </Box>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack
              direction="column"
              justifyContent="space-evenly"
              alignItems="stretch"
              spacing={4}
            >
              <Typography variant="body1">
                Chulalongkorn University has a policy for International students
                and researchers to have medical insurance to assure receiving
                essential medical services when needed. Thus, we would like to
                ask all international postdoctoral fellowship to have medical
                insurance while you are staying in Thailand. For those who
                already have medical insurance please fill this form, and
                attached your medical insurance's evidence in order to inform
                us.
              </Typography>

              <Typography variant="subtitle2">Information</Typography>

              <Stack direction="row" spacing={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Name of company that you have a medical insurance with*
                  </Typography>
                  <TextField
                    {...getFieldProps("name_company_medical_insurance")}
                    error={Boolean(
                      touched.name_company_medical_insurance &&
                        errors.name_company_medical_insurance
                    )}
                    helperText={
                      touched.name_company_medical_insurance &&
                      errors.name_company_medical_insurance
                    }
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Expiration date of your insurance *
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      views={["year", "month"]}
                      minDate={new Date()}
                      maxDate={new Date("2050/12/31")}
                      value={values.expiration_date_insurance}
                      onChange={(date) => {
                        if (isValidDate(date)) {
                          setFieldValue(
                            "expiration_date_insurance",
                            date.toISOString()
                          );
                        } else {
                          setFieldValue("expiration_date_insurance", date);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            touched.expiration_date_insurance &&
                              errors.expiration_date_insurance
                          )}
                          helperText={
                            touched.expiration_date_insurance &&
                            errors.expiration_date_insurance
                          }
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Stack>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Is the period of your medial insurance consistent with the
                  period of your C2F fellowship? *
                </Typography>
                <RadioGroup
                  row
                  {...getFieldProps("period_medical_insurance_c2f_scholarship")}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes, It is"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No, It is not"
                  />
                </RadioGroup>
                <span
                  style={{
                    color: "#FF4842",
                    lineHeight: 1.5,
                    fontSize: "0.75rem",
                    fontFamily: "sans-serif",
                    fontWeight: 400,
                    textAlign: "left",
                    marginTop: "3px",
                    marginRight: "14px",
                    marginBottom: 0,
                    marginLeft: "14px"
                  }}
                >
                  {errors.period_medical_insurance_c2f_scholarship}
                </span>
                <Typography variant="body1">
                  please note the period of your medical insurance must be
                  consistent with the period of your C2F scholarship.
                </Typography>
              </FormControl>

              <Stack direction="row" spacing={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">Insurance Card *</Typography>
                  <TextField
                    inputProps={{
                      accept:
                        "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                    }}
                    type="file"
                    onChange={(event) => {
                      setFieldValue(
                        `insurance_card.file`,
                        event.currentTarget.files[0]
                      );
                    }}
                    error={Boolean(
                      touched.insurance_card && errors.insurance_card
                    )}
                    helperText={touched.insurance_card && errors.insurance_card}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Insurance Policy *
                  </Typography>
                  <TextField
                    inputProps={{
                      accept:
                        "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                    }}
                    type="file"
                    onChange={(event) => {
                      setFieldValue(
                        `insurance_policy.file`,
                        event.currentTarget.files[0]
                      );
                    }}
                    error={Boolean(
                      touched.insurance_policy && errors.insurance_policy
                    )}
                    helperText={
                      touched.insurance_policy && errors.insurance_policy
                    }
                  />
                </FormControl>
              </Stack>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Others evidence that show you already have a medical insurance
                </Typography>
                <TextField
                  inputProps={{
                    accept:
                      "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                  }}
                  type="file"
                  onChange={(event) => {
                    setFieldValue(
                      `others_evidence_medical_insurance.file`,
                      event.currentTarget.files[0]
                    );
                  }}
                  error={Boolean(
                    touched.others_evidence_medical_insurance &&
                      errors.others_evidence_medical_insurance
                  )}
                  helperText={
                    touched.others_evidence_medical_insurance &&
                    errors.others_evidence_medical_insurance
                  }
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">Remarks</Typography>
                <TextField
                  multiline
                  rows={5}
                  {...getFieldProps("remarks")}
                  error={Boolean(touched.remarks && errors.remarks)}
                  helperText={touched.remarks && errors.remarks}
                />
              </FormControl>

              <Stack
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={12}
                sx={{ paddingTop: 3 }}
              >
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
